import { useState } from 'react'
import { isDeveloperAdmin } from '@versports/access'
import { Navigate, Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { openVrApp, vrBridgeError } from '../../auth/vrBridge'
import { useAuth } from '../../auth/AuthContext'
import { canConfigureDevices, canSeeAdmin } from '../../auth/adminAccess'
import { AboutButton } from '@versports/ui'
import { AppNavBar } from '../../components/AppNavBar'
import { VerSportsLogo } from '@versports/ui'
import { ImpersonationBanner } from '../../components/ImpersonationBanner'

/**
 * Admin shell. Top bar with VerSports brand + horizontal nav (Inicio,
 * Clubes, Usuarios, Sesiones, Cuestionarios, Invitaciones, Eventos, Emails), main
 * content area, footer. Mirrors the layout of the old Jinja2 /admin so
 * muscle memory carries over.
 */
export function AdminLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [openingEditor, setOpeningEditor] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)

  async function openLevelEditor() {
    setOpeningEditor(true)
    setEditorError(null)
    try { await openVrApp('level-editor') }
    catch (error) { setEditorError(vrBridgeError(error)); setOpeningEditor(false) }
  }

  const showBusiness = canSeeAdmin(user)
  const showDevices = canConfigureDevices(user)

  // A pure systems admin (devices only, no business access) landing on the
  // admin index — which renders business stats — is bounced to their section.
  if (!showBusiness && showDevices && location.pathname === '/admin') {
    return <Navigate to="/admin/devices" replace />
  }

  const backendDocsUrl = (() => {
    const apiBase = import.meta.env.VITE_API_BASE ?? '/api'
    // Strip a trailing "/api" (leaves "" for the default same-origin "/api",
    // or "https://host" for an absolute VITE_API_BASE). Concatenating
    // basePath directly (no forced leading "/") avoids the "//docs" that a
    // naive `${basePath || '/'}/docs` produces when basePath is "" — browsers
    // treat a leading "//" as a scheme-relative URL (navigates to host
    // "docs"), which is exactly why this button 404'd.
    const basePath = apiBase.replace(/\/+$/, '').replace(/\/api$/, '')
    return `${basePath}/docs`
  })()

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <ImpersonationBanner />
      <header className="border-b-2 border-primary bg-surface shadow-[0_12px_36px_rgba(226,90,27,0.12)]">
        <div className="mx-auto flex max-w-7xl flex-col gap-sm px-lg py-md md:flex-row md:items-center md:justify-between md:gap-md">
          <div className="flex items-center justify-between gap-md md:justify-start md:gap-lg">
            <Link to="/" className="no-underline">
              <VerSportsLogo />
              <span className="sr-only">VerSports</span>
            </Link>
            <span className="text-sm font-medium uppercase tracking-wide text-text-muted">
              admin
            </span>
            <div className="flex items-center gap-md text-sm md:hidden">
              <span className="text-text-muted">{user?.username}</span>
              <button
                type="button"
                onClick={() => void logout()}
                className="btn-ghost px-md py-xs text-xs"
              >
                Salir
              </button>
            </div>
          </div>
          <nav aria-label="Secciones de administración" className="-mx-lg flex min-w-0 gap-md overflow-x-auto px-lg text-sm md:mx-0 md:flex-1 md:px-0">
            {showBusiness && (
              <>
                <AdminNav to="/admin" label="Inicio" end />
                {/* Clubes va segundo: el club es la unidad de negocio (el
                    cliente que contrata), así que su lista es la vista de
                    operación más consultada después del tablero. Hasta
                    2026-09-08 no estaba en la barra y sólo se llegaba desde
                    un botón en el header de /admin/users — mg: "no hay
                    ningún lugar donde se vean los clubes". */}
                <AdminNav to="/admin/clubs" label="Clubes" />
                <AdminNav to="/admin/users" label="Usuarios" />
                <AdminNav to="/admin/sessions" label="Sesiones" />
                <AdminNav to="/admin/questionnaires" label="Cuestionarios" />
                <AdminNav to="/admin/cohorte" label="Cohorte" />
                <AdminNav to="/admin/invitations" label="Invitaciones" />
                <AdminNav to="/admin/events" label="Eventos" />
                <AdminNav to="/admin/emails" label="Emails" />
              </>
            )}
            {showDevices && <AdminNav to="/admin/devices" label="Sensores" />}
            <AdminNav to="/admin/setup" label="Setup" />
            {showBusiness && <AdminNav to="/admin/apps" label="Apps" />}
            {showBusiness && (
              <a
                href={backendDocsUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-text-muted hover:text-text-secondary"
              >
                Swagger
              </a>
            )}
          </nav>
          {isDeveloperAdmin(user) && (
            <details className="relative shrink-0 text-sm">
              <summary className="cursor-pointer text-text-muted hover:text-text-secondary">Superadmin</summary>
              <div className="absolute right-0 z-50 mt-sm min-w-48 rounded-lg border border-primary bg-surface p-md shadow-lg">
                <button type="button" onClick={() => void openLevelEditor()} disabled={openingEditor}
                  className="whitespace-nowrap text-text-primary disabled:opacity-50">
                  {openingEditor ? 'Abriendo…' : 'Level editor'}
                </button>
                {editorError && <p role="alert" className="mt-sm text-sm text-danger">{editorError}</p>}
              </div>
            </details>
          )}
          <div className="hidden items-center gap-md text-sm md:flex">
            <span className="text-text-muted">{user?.username}</span>
            <button
              type="button"
              onClick={() => void logout()}
              className="btn-ghost px-md py-xs text-xs"
            >
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-lg pb-24 pt-xl">
        <Outlet />
      </main>

      <AboutButton navOffset />
      <AppNavBar />
    </div>
  )
}

function AdminNav({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          'shrink-0 transition-colors',
          isActive
            ? 'font-medium text-text-primary'
            : 'text-text-muted hover:text-text-secondary',
        ].join(' ')
      }
    >
      {label}
    </NavLink>
  )
}
